import React, { useLayoutEffect, useRef } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

// replace these with your two ring image assets
import ringsOuterImage from '../../Assets/testimonial/outerring.webp';
import ringsInnerImage from '../../Assets/testimonial/innerring.webp';

gsap.registerPlugin(ScrollTrigger);

const IntegrationRings = () => {
  const sceneRef = useRef(null);   // the section that gets pinned
  const outerRef = useRef(null);   // outer ring image
  const innerRef = useRef(null);   // inner ring image

  useLayoutEffect(() => {
    const scene = sceneRef.current;
    const outer = outerRef.current;
    const inner = innerRef.current;

    if (!scene || !outer || !inner) return undefined;

    const ctx = gsap.context(() => {
      // start state
      gsap.set(outer, { rotate: 0, scale: 1, opacity: 1, transformOrigin: '50% 50%' });
      gsap.set(inner, { rotate: 0, scale: 1, opacity: 1, transformOrigin: '50% 50%' });

      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: scene,
          start: 'top top',
          end: '+=150%',       // how long the scroll-scrub lasts — bigger = slower animation
          scrub: 1.75,
          pin: true,
          pinSpacing: true,
          anticipatePin: 1,
          invalidateOnRefresh: true,
        },
      });

      // outer ring spins left, inner ring spins right — both zoom out & fade
      tl.to(outer, { rotate: -200, scale: 4.2, opacity: 0.75, ease: 'none' }, 0);
      tl.to(inner, { rotate: 200, scale: 4.2, opacity: 0.75, ease: 'none' }, 0);

      ScrollTrigger.refresh();

      const refresh = () => ScrollTrigger.refresh();
      window.addEventListener('load', refresh);
      window.addEventListener('resize', refresh);

      return () => {
        window.removeEventListener('load', refresh);
        window.removeEventListener('resize', refresh);
        tl.scrollTrigger?.kill();
        tl.kill();
      };
    }, scene);

    return () => ctx.revert();
  }, []);

  return (
    <section
      ref={sceneRef}
      className="
        relative z-[1] flex h-screen w-screen items-center justify-center
        overflow-hidden bg-white ml-[calc(50%-50vw)] pt-20
        mobile:min-h-0 mobile:py-3 mobile:pb-[38px]
      "
    >
      <div
        className="
          relative aspect-square
          w-[350px]
          md:w-[650px]
          lg:w-[700px]
          xl:w-[980px]
        "
      >
        {/* outer ring — only this animates */}
        <img
          ref={outerRef}
          src={ringsOuterImage}
          alt=""
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 block h-full w-full will-change-transform"
        />

        {/* inner ring — only this animates */}
        <img
          ref={innerRef}
          src={ringsInnerImage}
          alt=""
          aria-hidden="true"
          className="
            pointer-events-none absolute left-[49%] top-[46.5%] block
            h-[75%] w-[75%] -translate-x-1/2 -translate-y-1/2 will-change-transform
          "
        />

        {/* center heading — completely static, never animated */}
        <h3
          style={{ fontFamily: "'Playfair Display', serif" }}
          className="
            pointer-events-none absolute left-1/2 top-1/2 z-[10] m-0
            -translate-x-1/2 -translate-y-1/2 whitespace-nowrap text-center
            text-[20px] font-bold italic leading-[1.1] text-[#121212]
            md:text-[40px] lg:text-[45px] xl:text-[60px]
          "
        >
          All Your Tools; <br />
          <span
            className="
              bg-[linear-gradient(140.02deg,#ffc951_2.22%,#c94daa_96.12%)]
              bg-clip-text text-transparent
            "
          >
            One Integration.
          </span>
        </h3>
      </div>
    </section>
  );
};

export default IntegrationRings;